import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";
import { api, errMsg, fieldErrs } from "@/lib/api";
import { addDays } from "@/lib/dates";
import { PageLoader } from "@/lib/hooks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AdminLayout, btnPrimary, btnSecondary, ConfirmDelete, FieldError, inputCls, Panel } from "@/components/admin/AdminLayout";
import { APPT_STATUSES } from "@/components/admin/AppointmentCard";

const REQUIRED = "This field is required";
const blank = { name: "", phone: "", email: "", vehicle: "", job: "", dropoff_date: "", dropoff_time: "", pickup_date: "", notes: "", status: "Scheduled", message_id: null };

const validate = (f) => {
  const e = {};
  if (!f.name.trim()) e.name = REQUIRED;
  if (!f.phone.trim()) e.phone = REQUIRED;
  if (!f.dropoff_date) e.dropoff_date = REQUIRED;
  if (f.dropoff_date && f.pickup_date && f.pickup_date < f.dropoff_date) e.pickup_date = "Pickup date can't be before drop-off date";
  return e;
};

const F = ({ label, error, id, children }) => (
  <div className="min-w-0">
    <Label htmlFor={id} className="text-sm text-zinc-200">{label}</Label>
    <div className="mt-1.5">{children}</div>
    <FieldError msg={error} testId={`${id}-error`} />
  </div>
);

export default function AppointmentFormPage() {
  const { appointmentId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const isNew = !appointmentId;
  const fromMessage = params.get("message");
  const [f, setF] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!isNew) return (await api.get(`/admin/appointments/${appointmentId}`)).data;
      const date = params.get("date") || "";
      const base = { ...blank, dropoff_date: date, pickup_date: date ? addDays(date, 4) : "" };
      if (!fromMessage) return base;
      const m = (await api.get(`/admin/messages/${fromMessage}`)).data;
      return { ...base, name: m.name, phone: m.phone, email: m.email, vehicle: m.vehicle, job: m.service, notes: m.message, message_id: m.id };
    };
    load().then((v) => alive && setF((cur) => cur ?? v)).catch(() => navigate("/admin/appointments", { replace: true }));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!f) return <PageLoader testId="appointment-form-loading" />;
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const setDropoff = (e) => {
    const d = e.target.value;
    const autoPickup = !f.pickup_date || (f.dropoff_date && f.pickup_date === addDays(f.dropoff_date, 4));
    setF({ ...f, dropoff_date: d, pickup_date: d && autoPickup ? addDays(d, 4) : f.pickup_date });
  };
  const input = (k, label, type = "text", extra = {}) => (
    <F label={label} id={`appt-${k}`} error={errors[k]}>
      <Input id={`appt-${k}`} type={type} value={f[k]} onChange={extra.onChange || set(k)} className={inputCls} data-testid={`appointment-${k}-input`} />
    </F>
  );

  const save = async (e) => {
    e.preventDefault();
    const errs = validate(f);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const { data } = isNew ? await api.post("/admin/appointments", f) : await api.put(`/admin/appointments/${appointmentId}`, f);
      toast.success("Appointment saved");
      navigate(isNew && fromMessage ? `/admin/messages/${fromMessage}` : `/admin/appointments?date=${data.dropoff_date}`, { replace: true });
    } catch (err) {
      setErrors(fieldErrs(err));
      toast.error(errMsg(err));
      setBusy(false);
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/admin/appointments/${appointmentId}`);
      toast.success("Appointment deleted");
      navigate(`/admin/appointments?date=${f.dropoff_date}`, { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <AdminLayout title={isNew ? "New appointment" : "Edit appointment"} back={fromMessage ? `/admin/messages/${fromMessage}` : "/admin/appointments"} backLabel={fromMessage ? "Message" : "Appointments"} testId="admin-appointment-form-page">
      <form onSubmit={save} noValidate data-testid="appointment-form">
        <Panel title="When">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {input("dropoff_date", "Drop-off date", "date", { onChange: setDropoff })}
            {input("dropoff_time", "Drop-off time (optional)", "time")}
            {input("pickup_date", "Est. pickup date", "date")}
          </div>
        </Panel>
        <Panel title="Customer">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {input("name", "Name")}
            {input("phone", "Phone", "tel")}
            {input("email", "Email (optional)", "email")}
            {input("vehicle", "Vehicle (optional)")}
          </div>
          <div className="mt-4 space-y-4">
            {input("job", "Job (optional)")}
            <F label="Notes (optional)" id="appt-notes">
              <Textarea id="appt-notes" rows={4} value={f.notes} onChange={set("notes")} className="bg-[#121214] border-zinc-700 text-base" data-testid="appointment-notes-input" />
            </F>
          </div>
        </Panel>
        <Panel title="Status">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5" role="radiogroup">
            {APPT_STATUSES.map((s) => (
              <button key={s} type="button" role="radio" aria-checked={f.status === s} onClick={() => set("status")(s)}
                className={`min-h-[48px] rounded-md border px-2 text-sm font-medium transition-colors ${f.status === s ? "border-[#FFD23F] bg-[#FFD23F] text-[#121214]" : "border-zinc-700 text-zinc-200 hover:bg-zinc-800"}`}
                data-testid={`appointment-status-${s.toLowerCase().replace(/ /g, "-")}`}>
                {s}
              </button>
            ))}
          </div>
        </Panel>
        <div className="sticky bottom-0 -mx-4 flex gap-2 border-t border-zinc-800 bg-[#121214]/95 px-4 py-3 backdrop-blur">
          <Button type="submit" disabled={busy} className={`${btnPrimary} flex-1`} data-testid="appointment-save-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save appointment
          </Button>
          {!isNew && (
            <ConfirmDelete description="This appointment will be removed from the calendar." onConfirm={remove}
              trigger={<Button type="button" className={`${btnSecondary} text-[#ff8a80]`} aria-label="Delete appointment" data-testid="appointment-delete-button"><Trash2 className="h-4 w-4" /></Button>} />
          )}
        </div>
      </form>
    </AdminLayout>
  );
}
